import axios from 'axios';
import { BASE_URL } from './api';

// The queue a URL-discovery run leaves behind: companies it couldn't match to
// a website on its own. Deciding a row writes the company's website (or marks
// it as having none) straight away; undo puts the company back as it was.
// Claude reviews the same queue through the MCP tools, as source 'ai'.

// params: { status, kind, run, min_confidence, max_confidence, has_proposal,
// has_cage, flagged, source, q, page, page_size }. status defaults to pending
// server-side; 'any' lists every status.
export async function getUrlReviews(token, params = {}) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/url_reviews/`,
        headers: { 'Authorization': `Token ${token}` },
        params,
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// One row with everything needed to decide it: the company, every candidate the
// run looked at (index is what decide's candidate_index takes), sites that
// couldn't be fetched but are indexed, the CAGE record and the row's history.
export async function getUrlReview(reviewId, token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/url_reviews/${reviewId}/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// { counts: [{ status, kind, count }] }, across every run or for one.
export async function getUrlReviewCounts(token, runId = null) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/url_reviews/counts/`,
        headers: { 'Authorization': `Token ${token}` },
        params: runId ? { run: runId } : {},
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Discovery runs, newest first, with how many of each one's rows are pending
// and decided.
export async function getUrlDiscoveryRuns(token) {
    return axios({
        method: 'get',
        url: `${BASE_URL}/url_reviews/runs/`,
        headers: { 'Authorization': `Token ${token}` },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// decision: { decision: 'url' | 'no_url', candidate_index?, url?, confidence?,
// reasoning? }. A 'url' decision needs candidate_index or url. Refusals come
// back as 409 { code, detail }: not_pending, or superseded when the company got
// a website some other way first (the row is closed, nothing overwritten).
export async function decideUrlReview(reviewId, decision, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/url_reviews/${reviewId}/decide/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { ...decision, source: 'human' },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Set a row aside without deciding it. Nothing on the company changes.
export async function skipUrlReview(reviewId, token, reasoning = '') {
    return axios({
        method: 'post',
        url: `${BASE_URL}/url_reviews/${reviewId}/skip/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { source: 'human', reasoning },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

export async function restoreUrlReview(reviewId, token) {
    return axios({
        method: 'post',
        url: `${BASE_URL}/url_reviews/${reviewId}/restore/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { source: 'human' },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}

// Put the company's website and vetting back as they were before the decision.
// 409 conflict when the website has changed since.
export async function undoUrlReview(reviewId, token, reasoning = '') {
    return axios({
        method: 'post',
        url: `${BASE_URL}/url_reviews/${reviewId}/undo/`,
        headers: { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' },
        data: { source: 'human', reasoning },
    }).then((response) => response.data)
    .catch((error) => { throw error });
}
